import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, Key, User, ArrowRight, Lightbulb, AlertCircle } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

interface AIPackage {
  id: string;
  name: string;
  version: string;
  price: number;
  description: string;
}

const RegisterSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [aiPackages, setAiPackages] = useState<AIPackage[]>([]);
  const [userPackages, setUserPackages] = useState<any[]>([]);
  
  const username = searchParams.get('username') || '';
  const password = searchParams.get('password') || '';
  const email = searchParams.get('email') || '';
  const plan = searchParams.get('plan') || 'free';
  const source = searchParams.get('source') || 'edumi';
  const packageNameFromUrl = searchParams.get('packageName') || '';
  
  // Get discount info from URL params
  const discountCodeFromUrl = searchParams.get('discountCode') || '';
  const discountPercentFromUrl = searchParams.get('discountPercent') ? parseInt(searchParams.get('discountPercent')!) : null;
  const originalAmountFromUrl = searchParams.get('originalAmount') ? parseInt(searchParams.get('originalAmount')!) : null;
  const finalAmountFromUrl = searchParams.get('finalAmount') ? parseInt(searchParams.get('finalAmount')!) : null;
  
  // Get checkout data from localStorage - check all possible keys
  const subscriptionData = localStorage.getItem('subscriptionCheckout');
  const salePageData = localStorage.getItem('salePageCheckout');
  const coursedeeData = localStorage.getItem('coursedeeCartCheckout');
  
  let savedCheckoutData: any = null;
  
  // Try to parse from different sources
  if (coursedeeData) {
    try {
      savedCheckoutData = JSON.parse(coursedeeData);
      console.log('[RegisterSuccess] Using coursedeeCartCheckout:', savedCheckoutData);
    } catch (err) {
      console.error('Error parsing coursedee checkout data:', err);
    }
  } else if (salePageData) {
    try {
      savedCheckoutData = JSON.parse(salePageData);
      console.log('[RegisterSuccess] Using salePageCheckout:', savedCheckoutData);
    } catch (err) {
      console.error('Error parsing salepage checkout data:', err);
    }
  } else if (subscriptionData) {
    try {
      savedCheckoutData = JSON.parse(subscriptionData);
      console.log('[RegisterSuccess] Using subscriptionCheckout:', savedCheckoutData);
    } catch (err) {
      console.error('Error parsing subscription checkout data:', err);
    }
  }
  
  // Determine actual plan and source from checkout data or hostname
  const actualPlan = savedCheckoutData?.tier || plan;
  const hostname = window.location.hostname;
  let actualSource = source;
  
  // Check from savedCheckoutData first
  if (savedCheckoutData?.source) {
    actualSource = savedCheckoutData.source;
  } else if (hostname.includes('aiforyou')) {
    actualSource = 'coursedee';
  } else if (hostname.includes('salepage')) {
    actualSource = 'salepage';
  } else if (source) {
    actualSource = source;
  } else {
    actualSource = 'edumi';
  }
  
  console.log('[RegisterSuccess] Source detection:', {
    hostname,
    searchParamSource: source,
    checkoutSource: savedCheckoutData?.source,
    finalSource: actualSource,
    checkoutData: savedCheckoutData
  });

  useEffect(() => {
    // If no data, redirect back
    if (!username || !email) {
      navigate('/');
      return;
    }
    
    // Fetch user's coursedeePackages from Firestore
    const fetchUserPackages = async () => {
      try {
        const user = auth.currentUser;
        if (user && actualSource === 'coursedee') {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const packages = userData.coursedeePackages || [];
            console.log('[RegisterSuccess] Fetched coursedeePackages:', packages);
            setUserPackages(packages);
          }
        }
      } catch (error) {
        console.error('[RegisterSuccess] Error fetching user packages:', error);
      }
    };
    
    fetchUserPackages();
    
    // Check if coming from email verification (has emailVerified param or user is verified)
    const checkEmailVerification = async () => {
      const user = auth.currentUser;
      
      // If user is logged in and email is verified, redirect to login
      if (user && user.emailVerified) {
        console.log('[RegisterSuccess] User email verified, redirecting to login...');
        
        // Determine login URL based on source
        let loginUrl = '/login';
        if (actualSource === 'coursedee') {
          loginUrl = 'https://aiforyou.web.app/login';
        } else if (actualSource === 'salepage') {
          loginUrl = 'https://saledi.web.app/login';
        } else {
          loginUrl = 'https://aiforyou.web.app/login';
        }
        
        console.log('[RegisterSuccess] Redirecting to:', loginUrl);
        
        // Redirect to login page
        setTimeout(() => {
          if (loginUrl.startsWith('http')) {
            window.location.href = loginUrl;
          } else {
            navigate(loginUrl);
          }
        }, 500);
        
        return;
      }
    };
    
    checkEmailVerification();
    
    // Fetch AI packages from Firestore
    const fetchAIPackages = async () => {
      try {
        const packagesSnapshot = await getDocs(collection(db, 'aiPackages'));
        const packagesData = packagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AIPackage[];
        setAiPackages(packagesData);
      } catch (error) {
        console.error('Error fetching AI packages:', error);
      }
    };
    
    fetchAIPackages();
  }, [username, email, navigate, actualSource]);

  const handleGoToDashboard = () => {
    let redirectUrl = 'https://aiforyou.web.app/';
    if (actualSource === 'coursedee') {
      redirectUrl = 'https://aiforyou.web.app/';
    } else if (actualSource === 'salepage') {
      redirectUrl = 'https://saledi.web.app/';
    }
    
    console.log('[RegisterSuccess] Redirecting to home:', redirectUrl);
    window.location.href = redirectUrl;
  };

  const getPlanName = () => {
    const isFreeActivation = savedCheckoutData?.isFreeActivation === true;
    
    // Debug logging
    console.log('[RegisterSuccess v2.4] getPlanName - actualSource:', actualSource);
    console.log('[RegisterSuccess v2.4] getPlanName - actualPlan:', actualPlan);
    console.log('[RegisterSuccess v2.4] getPlanName - plan:', plan);
    console.log('[RegisterSuccess v2.4] getPlanName - packageNameFromUrl:', packageNameFromUrl);
    console.log('[RegisterSuccess v2.4] getPlanName - savedCheckoutData:', savedCheckoutData);
    console.log('[RegisterSuccess v2.4] getPlanName - userPackages:', userPackages);
    
    // Check if this is Coursedee package FIRST (highest priority)
    if (actualSource === 'coursedee') {
      // Priority 1: Use items from savedCheckoutData (cart checkout) - EXACT NAMES
      if (savedCheckoutData?.items && savedCheckoutData.items.length > 0) {
        const packageNames = savedCheckoutData.items.map((item: any) => item.name).join(', ');
        console.log('[RegisterSuccess v2.6] Using savedCheckoutData.items:', packageNames);
        return packageNames || 'AIForYou';
      }
      
      // Priority 2: Use userPackages from Firestore
      if (userPackages && userPackages.length > 0) {
        const packageNames = userPackages.map((pkg: any) => pkg.name).join(', ');
        console.log('[RegisterSuccess v2.6] Using userPackages:', packageNames);
        return packageNames || 'AIForYou';
      }
      
      // Priority 3: Use packageName from URL (for both paid and free with discount code)
      if (packageNameFromUrl) {
        console.log('[RegisterSuccess v2.6] Using packageNameFromUrl:', packageNameFromUrl);
        // If packageName is short form (e.g., "AIForYou V1"), expand to full name
        if (packageNameFromUrl === 'AIForYou V1') {
          return 'AIForYou Content Builder (V1)';
        } else if (packageNameFromUrl === 'AIForYou V2') {
          return 'AIForYou Brand Builder (V2)';
        }
        return packageNameFromUrl;
      }
      
      // Priority 4: If has tier info (single package), map to package name
      if (actualPlan && actualPlan !== 'free') {
        if (actualPlan.includes('v2')) return 'AIForYou Brand Builder (V2)';
        if (actualPlan.includes('v1')) return 'AIForYou Content Builder (V1)';
      }
      
      // Priority 5: Check if this is FREE TRIAL (no discount code, no purchase)
      if (plan === 'free' && !isFreeActivation && !savedCheckoutData && (!userPackages || userPackages.length === 0)) {
        console.log('[RegisterSuccess v2.6] Free trial detected');
        return 'AIForYou (ทดลองใช้ฟรี)';
      }
      
      // Default for Coursedee
      return 'AIForYou';
    }
    
    // For EduMi and Salepage: Show Pro+/Pro tiers
    if (actualPlan.includes('pro_plus_yearly')) return 'Pro+ Yearly';
    if (actualPlan.includes('pro_plus_semi_annual')) return 'Pro+ Semi-Annual';
    if (actualPlan.includes('pro_plus_monthly')) return 'Pro+ Monthly';
    if (actualPlan.includes('pro_yearly')) return 'Pro Yearly';
    if (actualPlan.includes('pro_semi_annual')) return 'Pro Semi-Annual';
    if (actualPlan.includes('pro_monthly')) return 'Pro Monthly';
    
    // Regular free trial (no discount code)
    if (plan === 'free' && !isFreeActivation) return 'FREE 14 วัน';
    
    return 'แพ็คเกจที่เลือก';
  };

  const getPlanPrice = () => {
    // Priority 1: Check for discount - if 100% discount, show FREE
    const discountPercent = discountPercentFromUrl ?? savedCheckoutData?.discountPercent;
    const finalAmount = finalAmountFromUrl ?? savedCheckoutData?.finalAmount;
    const originalAmount = originalAmountFromUrl ?? savedCheckoutData?.originalAmount;
    
    // If 100% discount or free activation, show FREE
    if (discountPercent === 100 || savedCheckoutData?.isFreeActivation) {
      return 'FREE';
    }
    
    // If has finalAmount (actual paid price), use it - show exact amount even if 0
    if (finalAmount !== null && finalAmount !== undefined) {
      // For 99% discount or other discounts, show the actual final price
      if (discountPercent && discountPercent > 0 && discountPercent < 100) {
        return `${finalAmount.toLocaleString()} บาท (ส่วนลด ${discountPercent}%)`;
      }
      return finalAmount > 0 ? `${finalAmount.toLocaleString()} บาท` : 'FREE';
    }
    
    // For Coursedee: Calculate total price from userPackages
    if (actualSource === 'coursedee' && userPackages && userPackages.length > 0) {
      const totalPrice = userPackages.reduce((sum: number, pkg: any) => sum + (pkg.price || 0), 0);
      
      // Apply discount if exists
      if (discountPercent && discountPercent > 0) {
        const discountedPrice = Math.max(0, totalPrice - (totalPrice * discountPercent / 100));
        // For 99% discount or other discounts, show the actual final price with discount percentage
        if (discountPercent < 100) {
          return `${discountedPrice.toLocaleString()} บาท (ส่วนลด ${discountPercent}%)`;
        }
        return discountedPrice > 0 ? `${discountedPrice.toLocaleString()} บาท` : 'FREE';
      }
      
      return totalPrice > 0 ? `${totalPrice.toLocaleString()} บาท` : 'ฟรี';
    }
    
    // For Coursedee: Calculate total price from all items
    if (actualSource === 'coursedee' && savedCheckoutData?.items && savedCheckoutData.items.length > 0) {
      const totalPrice = savedCheckoutData.items.reduce((sum: number, item: any) => sum + (item.price || 0), 0);
      
      // Apply discount if exists
      if (discountPercent && discountPercent > 0) {
        const discountedPrice = Math.max(0, totalPrice - (totalPrice * discountPercent / 100));
        // For 99% discount or other discounts, show the actual final price with discount percentage
        if (discountPercent < 100) {
          return `${discountedPrice.toLocaleString()} บาท (ส่วนลด ${discountPercent}%)`;
        }
        return discountedPrice > 0 ? `${discountedPrice.toLocaleString()} บาท` : 'FREE';
      }
      
      return totalPrice > 0 ? `${totalPrice.toLocaleString()} บาท` : 'ฟรี';
    }
    
    // For free trial
    if (plan === 'free' && !discountPercent) {
      return 'ฟรี';
    }
    
    // Get price from checkout data with discount applied
    if (savedCheckoutData?.amount !== null && savedCheckoutData?.amount !== undefined) {
      return savedCheckoutData.amount > 0 ? `${savedCheckoutData.amount.toLocaleString()} บาท` : 'FREE';
    }
    
    return '-';
  };

  const getDiscount = () => {
    const discountCode = discountCodeFromUrl || savedCheckoutData?.discountCode;
    const discountPercent = discountPercentFromUrl ?? savedCheckoutData?.discountPercent;
    
    // If has discount code and percentage
    if (discountCode && discountPercent !== null && discountPercent !== undefined) {
      return `${discountCode} (ส่วนลด ${discountPercent}%)`;
    }
    
    // If has discount code only (backward compatibility)
    if (discountCode) {
      return discountCode;
    }
    
    return 'ไม่มี';
  };

  const getSourceName = () => {
    if (actualSource === 'coursedee') return 'AIForYou';
    if (actualSource === 'salepage') return 'Salepage.edumi.co';
    return 'EduMi.co';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            สมัครสมาชิกเรียบร้อยแล้ว!
          </h1>
          <p className="text-xl text-gray-600">
            ตรวจสอบอีเมลเพื่อยืนยันตัวตนและเริ่มใช้งานค่ะ
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Email Verification Section */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-100 p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  ตรวจสอบอีเมลของคุณ
                </h2>
                <p className="text-gray-700 mb-3">
                  เราได้ส่งอีเมลยืนยันตัวตนไปที่:
                </p>
                <div className="bg-white rounded-lg px-4 py-3 border-2 border-blue-200">
                  <p className="font-bold text-blue-600">{email}</p>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  กรุณาคลิกลิงก์ในอีเมลเพื่อยืนยันตัวตนและเริ่มใช้งาน
                </p>
              </div>
            </div>
          </div>

          {/* Login Credentials Section */}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Key className="w-6 h-6 text-purple-600" />
              ข้อมูลการเข้าสู่ระบบ
            </h2>

            <div className="space-y-4">
              {/* Email */}
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-600">Email (Username)</span>
                </div>
                <p className="text-xl font-bold text-gray-900 ml-8">{username}</p>
              </div>

              {/* Password */}
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <Key className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-600">Password</span>
                </div>
                <p className="text-xl font-bold text-gray-900 ml-8 font-mono">{password}</p>
                <p className="text-xs text-gray-500 mt-2 ml-8">
                  (6 ตัวเลขสุดท้ายของเบอร์โทรศัพท์)
                </p>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleGoToDashboard}
              className="w-full mt-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              เริ่มใช้งานเลย
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 space-y-4">
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
            <p className="text-sm text-yellow-900 flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>เคล็ดลับ:</strong> คุณสามารถเปลี่ยนรหัสผ่านได้ภายหลังในหน้าโปรไฟล์
              </span>
            </p>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-900 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>ไม่เจออีเมล?</strong> ตรวจสอบในโฟลเดอร์ Spam หรือ Junk Mail 
                หรือติดต่อฝ่ายสนับสนุนได้ที่ support@edumi.co
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterSuccess;
